'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MortgageCalculator() {
  const t = useTranslations("tools.mortgageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    homePrice: 400000,
    downPayment: 20,
    downPaymentUnit: 'percent',
    loanTerm: 30,
    interestRate: 6.5,
    includeCosts: true,
    propertyTax: 1.2,
    propertyTaxUnit: 'percent',
    homeInsurance: 1500,
    homeInsuranceUnit: 'dollar',
    pmi: 0,
    pmiUnit: 'dollar',
    hoaFee: 0,
    hoaFeeUnit: 'dollar',
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    handleCalculate();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const getValueInDollars = (value: number, unit: string, baseAmount: number) => {
    if (unit === 'percent') {
      return (value / 100) * baseAmount;
    }
    return value;
  };

  const handleCalculate = () => {
    const homePrice = inputs.homePrice || 0;
    const downPaymentValue = inputs.downPayment || 0;
    const downPaymentUnit = inputs.downPaymentUnit;
    const loanTerm = inputs.loanTerm || 30;
    const interestRate = inputs.interestRate || 0;
    const includeCosts = inputs.includeCosts;

    const downPaymentDollars = getValueInDollars(downPaymentValue, downPaymentUnit, homePrice);
    const loanAmount = homePrice - downPaymentDollars;

    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;

    let monthlyPI = 0;
    if (monthlyRate > 0) {
      monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
                 (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    } else {
      monthlyPI = loanAmount / numberOfPayments;
    }

    const totalPayments = monthlyPI * numberOfPayments;
    const totalInterest = totalPayments - loanAmount;

    let monthlyTax = 0;
    let monthlyInsurance = 0;
    let monthlyPMI = 0;
    let monthlyHOA = 0;

    if (includeCosts) {
      const propertyTaxValue = inputs.propertyTax || 0;
      const propertyTaxUnit = inputs.propertyTaxUnit;
      const annualTax = getValueInDollars(propertyTaxValue, propertyTaxUnit, homePrice);
      monthlyTax = annualTax / 12;

      const homeInsuranceValue = inputs.homeInsurance || 0;
      const homeInsuranceUnit = inputs.homeInsuranceUnit;
      const annualInsurance = getValueInDollars(homeInsuranceValue, homeInsuranceUnit, homePrice);
      monthlyInsurance = annualInsurance / 12;

      const pmiValue = inputs.pmi || 0;
      const pmiUnit = inputs.pmiUnit;
      const annualPMI = getValueInDollars(pmiValue, pmiUnit, loanAmount);
      monthlyPMI = annualPMI / 12;

      const hoaFeeValue = inputs.hoaFee || 0;
      const hoaFeeUnit = inputs.hoaFeeUnit;
      const annualHOA = getValueInDollars(hoaFeeValue, hoaFeeUnit, homePrice);
      monthlyHOA = annualHOA / 12;
    }

    const totalMonthlyPayment = monthlyPI + monthlyTax + monthlyInsurance + monthlyPMI + monthlyHOA;

    const totalCost = loanAmount + totalInterest;
    const principalPercent = (loanAmount / totalCost) * 100;
    const interestPercent = (totalInterest / totalCost) * 100;

    setResults({
      totalMonthlyPayment,
      monthlyPI,
      loanAmount,
      downPaymentDollars,
      totalInterest,
      monthlyTax,
      monthlyInsurance,
      monthlyPMI,
      monthlyHOA,
      principalPercent,
      interestPercent,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCalculate();
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    background: '#f8fafc',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.75rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const sectionTitleStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.primary,
      marginBottom: '1rem',
      paddingBottom: '0.5rem',
      borderBottom: `2px solid #e0f2fe`,
    },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const formGroupStyle = { marginBottom: '1.25rem' };

  const labelStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 500,
      color: '#334155',
      marginBottom: '0.5rem',
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.75rem',
      border: `2px solid ${colors.border}`,
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s',
      outline: 'none',
    },
    {},
    {},
    {}
  );

  const selectStyle = {
    ...inputStyle,
    width: '80px',
    cursor: 'pointer',
  };

  const checkboxGroupStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem',
      background: colors.background,
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
    },
    {},
    {},
    {}
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      textAlign: 'center' as const,
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultHighlightValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const resultItemStyle = {
    padding: '1rem',
    background: colors.background,
    borderRadius: '0.5rem',
    borderLeft: `4px solid ${colors.primary}`,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const tooltipIconStyle = {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    background: '#94a3b8',
    color: 'white',
    borderRadius: '50%',
    textAlign: 'center' as const,
    lineHeight: '16px',
    fontSize: '12px',
    marginLeft: '0.5rem',
    cursor: 'help',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={subtitleStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={calculatorLayoutStyle}>
          <div style={formCardStyle}>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={sectionTitleStyle}>{t("loanDetailsTitle")}</div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="homePrice">{t("homePriceLabel")}</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                    <input
                      type="number"
                      id="homePrice"
                      value={inputs.homePrice}
                      onChange={(e) => setInputs({...inputs, homePrice: parseFloat(e.target.value) || 0})}
                      style={{...inputStyle, paddingLeft: '2rem'}}
                      min="0"
                      step="1000"
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="downPayment">
                    {t("downPaymentLabel")}
                    <span style={tooltipIconStyle} title={t("downPaymentTooltip")}>?</span>
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      {inputs.downPaymentUnit === 'percent' && (
                        <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                      )}
                      {inputs.downPaymentUnit === 'dollar' && (
                        <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                      )}
                      <input
                        type="number"
                        id="downPayment"
                        value={inputs.downPayment}
                        onChange={(e) => setInputs({...inputs, downPayment: parseFloat(e.target.value) || 0})}
                        style={{
                          ...inputStyle,
                          paddingRight: inputs.downPaymentUnit === 'percent' ? '2.5rem' : '0.75rem',
                          paddingLeft: inputs.downPaymentUnit === 'dollar' ? '2rem' : '0.75rem',
                        }}
                        min="0"
                        step="0.1"
                      />
                    </div>
                    <select
                      value={inputs.downPaymentUnit}
                      onChange={(e) => setInputs({...inputs, downPaymentUnit: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="percent">%</option>
                      <option value="dollar">$</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loanTerm">
                    {t("loanTermLabel")}
                    <span style={tooltipIconStyle} title={t("loanTermTooltip")}>?</span>
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="number"
                      id="loanTerm"
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseFloat(e.target.value) || 30})}
                      style={inputStyle}
                      min="1"
                      max="50"
                    />
                    <span style={{ whiteSpace: 'nowrap', color: colors.textSecondary }}>{t("yearsUnit")}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interestRate">
                    {t("interestRateLabel")}
                    <span style={tooltipIconStyle} title={t("interestRateTooltip")}>?</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                    <input
                      type="number"
                      id="interestRate"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      style={{...inputStyle, paddingRight: '2.5rem'}}
                      min="0"
                      max="30"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div style={checkboxGroupStyle}>
                <input
                  type="checkbox"
                  id="includeCosts"
                  checked={inputs.includeCosts}
                  onChange={(e) => setInputs({...inputs, includeCosts: e.target.checked})}
                  style={{ width: '20px', height: '20px', marginRight: '0.75rem', cursor: 'pointer' }}
                />
                <label htmlFor="includeCosts" style={{ margin: 0, cursor: 'pointer', fontWeight: 600, color: colors.textPrimary }}>
                  {t("includeCostsLabel")}
                </label>
              </div>

              {inputs.includeCosts && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={sectionTitleStyle}>{t("annualCostsTitle")}</div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="propertyTax">
                        {t("propertyTaxLabel")}
                        <span style={tooltipIconStyle} title={t("propertyTaxTooltip")}>?</span>
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          {inputs.propertyTaxUnit === 'percent' && (
                            <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                          )}
                          {inputs.propertyTaxUnit === 'dollar' && (
                            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                          )}
                          <input
                            type="number"
                            id="propertyTax"
                            value={inputs.propertyTax}
                            onChange={(e) => setInputs({...inputs, propertyTax: parseFloat(e.target.value) || 0})}
                            style={{
                              ...inputStyle,
                              paddingRight: inputs.propertyTaxUnit === 'percent' ? '2.5rem' : '0.75rem',
                              paddingLeft: inputs.propertyTaxUnit === 'dollar' ? '2rem' : '0.75rem',
                            }}
                            min="0"
                            step="0.1"
                          />
                        </div>
                        <select
                          value={inputs.propertyTaxUnit}
                          onChange={(e) => setInputs({...inputs, propertyTaxUnit: e.target.value})}
                          style={selectStyle}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="homeInsurance">
                        {t("homeInsuranceLabel")}
                        <span style={tooltipIconStyle} title={t("homeInsuranceTooltip")}>?</span>
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          {inputs.homeInsuranceUnit === 'dollar' && (
                            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                          )}
                          {inputs.homeInsuranceUnit === 'percent' && (
                            <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                          )}
                          <input
                            type="number"
                            id="homeInsurance"
                            value={inputs.homeInsurance}
                            onChange={(e) => setInputs({...inputs, homeInsurance: parseFloat(e.target.value) || 0})}
                            style={{
                              ...inputStyle,
                              paddingLeft: inputs.homeInsuranceUnit === 'dollar' ? '2rem' : '0.75rem',
                              paddingRight: inputs.homeInsuranceUnit === 'percent' ? '2.5rem' : '0.75rem',
                            }}
                            min="0"
                            step="100"
                          />
                        </div>
                        <select
                          value={inputs.homeInsuranceUnit}
                          onChange={(e) => setInputs({...inputs, homeInsuranceUnit: e.target.value})}
                          style={selectStyle}
                        >
                          <option value="dollar">$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="pmi">
                        {t("pmiLabel")}
                        <span style={tooltipIconStyle} title={t("pmiTooltip")}>?</span>
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          {inputs.pmiUnit === 'dollar' && (
                            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                          )}
                          {inputs.pmiUnit === 'percent' && (
                            <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                          )}
                          <input
                            type="number"
                            id="pmi"
                            value={inputs.pmi}
                            onChange={(e) => setInputs({...inputs, pmi: parseFloat(e.target.value) || 0})}
                            style={{
                              ...inputStyle,
                              paddingLeft: inputs.pmiUnit === 'dollar' ? '2rem' : '0.75rem',
                              paddingRight: inputs.pmiUnit === 'percent' ? '2.5rem' : '0.75rem',
                            }}
                            min="0"
                            step="100"
                          />
                        </div>
                        <select
                          value={inputs.pmiUnit}
                          onChange={(e) => setInputs({...inputs, pmiUnit: e.target.value})}
                          style={selectStyle}
                        >
                          <option value="dollar">$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="hoaFee">
                        {t("hoaFeeLabel")}
                        <span style={tooltipIconStyle} title={t("hoaFeeTooltip")}>?</span>
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          {inputs.hoaFeeUnit === 'dollar' && (
                            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>$</span>
                          )}
                          {inputs.hoaFeeUnit === 'percent' && (
                            <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontWeight: 500 }}>%</span>
                          )}
                          <input
                            type="number"
                            id="hoaFee"
                            value={inputs.hoaFee}
                            onChange={(e) => setInputs({...inputs, hoaFee: parseFloat(e.target.value) || 0})}
                            style={{
                              ...inputStyle,
                              paddingLeft: inputs.hoaFeeUnit === 'dollar' ? '2rem' : '0.75rem',
                              paddingRight: inputs.hoaFeeUnit === 'percent' ? '2.5rem' : '0.75rem',
                            }}
                            min="0"
                            step="50"
                          />
                        </div>
                        <select
                          value={inputs.hoaFeeUnit}
                          onChange={(e) => setInputs({...inputs, hoaFeeUnit: e.target.value})}
                          style={selectStyle}
                        >
                          <option value="dollar">$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                style={calculateButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t("calculateButton")}
              </button>
            </form>
          </div>

          {results && (
            <div style={resultsCardStyle}>
              <h3 style={h3Style}>{t("resultsTitle")}</h3>

              <div style={resultHighlightStyle}>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                  {t("totalMonthlyPaymentLabel")}
                </div>
                <div style={resultHighlightValueStyle}>
                  {formatCurrency(results.totalMonthlyPayment)}
                </div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>
                    {t("principalInterestLabel")}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary }}>
                    {formatCurrency(results.monthlyPI)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>
                    {t("loanAmountLabel")}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary }}>
                    {formatCurrency(results.loanAmount)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>
                    {t("downPaymentAmountLabel")}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary }}>
                    {formatCurrency(results.downPaymentDollars)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={{ display: 'block', fontSize: '0.85rem', color: colors.textSecondary, marginBottom: '0.25rem' }}>
                    {t("totalInterestLabel")}
                  </span>
                  <span style={{ display: 'block', fontSize: '1.25rem', fontWeight: 700, color: colors.textPrimary }}>
                    {formatCurrency(results.totalInterest)}
                  </span>
                </div>
              </div>

              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: `2px solid ${colors.border}` }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' }}>
                  {t("breakdownTitle")}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ color: colors.textSecondary }}>{t("principalInterestLabel")}</span>
                  <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatCurrency(results.monthlyPI)}</span>
                </div>
                {inputs.includeCosts && results.monthlyTax > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textSecondary }}>{t("propertyTaxLabel")}</span>
                    <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatCurrency(results.monthlyTax)}</span>
                  </div>
                )}
                {inputs.includeCosts && results.monthlyInsurance > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textSecondary }}>{t("homeInsuranceLabel")}</span>
                    <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatCurrency(results.monthlyInsurance)}</span>
                  </div>
                )}
                {inputs.includeCosts && results.monthlyPMI > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.textSecondary }}>{t("pmiLabel")}</span>
                    <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatCurrency(results.monthlyPMI)}</span>
                  </div>
                )}
                {inputs.includeCosts && results.monthlyHOA > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0' }}>
                    <span style={{ color: colors.textSecondary }}>{t("hoaFeeLabel")}</span>
                    <span style={{ fontWeight: 600, color: colors.textPrimary }}>{formatCurrency(results.monthlyHOA)}</span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '2rem', padding: '1.5rem', background: colors.background, borderRadius: '0.75rem' }}>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' }}>
                  {t("chartTitle")}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '140px', fontSize: '0.9rem', color: colors.textSecondary }}>
                      {t("principalLabel")}
                    </div>
                    <div style={{ flex: 1, height: '30px', background: colors.border, borderRadius: '0.25rem', overflow: 'hidden', position: 'relative' }}>
                      <div style={{
                        height: '100%',
                        width: `${results.principalPercent}%`,
                        background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                        transition: 'width 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: '0.5rem',
                      }}>
                        <span style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>
                          {formatCurrency(results.loanAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '140px', fontSize: '0.9rem', color: colors.textSecondary }}>
                      {t("interestLabel")}
                    </div>
                    <div style={{ flex: 1, height: '30px', background: colors.border, borderRadius: '0.25rem', overflow: 'hidden', position: 'relative' }}>
                      <div style={{
                        height: '100%',
                        width: `${results.interestPercent}%`,
                        background: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)',
                        transition: 'width 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: '0.5rem',
                      }}>
                        <span style={{ color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>
                          {formatCurrency(results.totalInterest)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep5")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <div style={{ ...paragraphStyle, background: '#fef3c7', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #f59e0b', marginBottom: '1rem' }}>
          <strong>{t("insightHighlight1Title")}</strong> {t("insightHighlight1")}
        </div>
        <div style={{ ...paragraphStyle, background: '#dbeafe', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #3b82f6', marginBottom: '1rem' }}>
          <strong>{t("insightHighlight2Title")}</strong> {t("insightHighlight2")}
        </div>
        <div style={{ ...paragraphStyle, background: '#dcfce7', padding: '1rem', borderRadius: '0.5rem', borderLeft: '4px solid #22c55e', marginBottom: '1rem' }}>
          <strong>{t("insightHighlight3Title")}</strong> {t("insightHighlight3")}
        </div>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle4")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph4")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle5")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph5")}</p>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={h3Style}>{t("faqQuestion6")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer6")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.wellsfargo.com/mortgage/calculators/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.nerdwallet.com/mortgages/calculators/mortgage-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.mortgagecalculator.org" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://yourhome.fanniemae.com/calculators-tools/mortgage-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t("reference4")}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.bankofamerica.com/mortgage/mortgage-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}